import React, { useEffect, useState } from 'react'
import { Box, Checkbox, OutlinedInput, Stack, Tooltip, styled } from '@mui/material'
import { useBoolean } from 'ahooks'

import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices'

import { commify, formatEther, formatUnits } from 'ethers/lib/utils.js'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useAccount, useNetwork } from 'wagmi'
import { useAtom } from 'jotai'
import Halo from '../../Halo'
import { CheckRowWrapper, LeftWrapper, MinimumAmountWrapper, Text1, Text2, Text3 } from '../styleds'
import Header from '../../Header'

import PendingHistory from './PendingHistory'
import ShowConfirmDialogButton from './ButtonFee'
import GoToWithdraw from './GoToWithdraw'
import NumberInput from '@/components/common/NumberInput'
import { getBrc20ContractAddress } from '@/utils/contract'
import { CHAIN_INFO } from '@/constants'
import { MaxButton } from '@/components/common/MaxButton'
import { TICKERS_FILTER_BY_CHAIN, TICKERS_MAP_BY_NAME } from '@/constants/token'

import { ReactComponent as BpIcon } from '@/assets/imgs/home/bpIcon.svg'
import { ReactComponent as BpCheckedIcon } from '@/assets/imgs/home/bpCheckedIcon.svg'

import AssetSelect from '@/components/common/AssetSelect2'
import BtcSelect from '@/components/common/BtcSelect'
import NetworkSelect from '@/components/common/NetworkSelect'
import useFee from '@/hooks/useFee'
import useQueryToken from '@/hooks/useQueryToken'
import { useBalanceOf } from '@/hooks/brc20Hooks'
import { getTokenDecimals } from '@/utils'
import { TargetChainAtom, TickerAtom } from '@/components/common/atoms'

const Tag = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 2px 4px;

  background: rgba(191, 255, 11, 0.1);
  border-radius: 4px;

  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.01em;
  font-feature-settings: 'ss01' on, 'case' on, 'calt' off;
  color: #ffffff;

  margin-right: 4px;
`

const Brc20Tag = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2px 8px;
  border: 1px solid rgba(191, 255, 11, 0.2);
`

export const WithdrawLeft = () => {
  const [isChecked, { toggle: toggleChecked }] = useBoolean(false)

  const [amountInput, setAmountInput] = useState('')
  const [addressInput, setAddressInput] = useState('')

  const { chain } = useNetwork()

  const [ticker, setTicker] = useAtom(TickerAtom)

  const { address: account } = useAccount()

  const [targetChain, setTargetChain] = useAtom(TargetChainAtom)

  const { data: brc20Balance, loading: isBrc20BalanceLoading } = useBalanceOf({
    address: account,
    token: getBrc20ContractAddress(ticker, chain?.id),
  })

  const fee = useFee(ticker)

  useQueryToken((ticker) => {
    setTicker(ticker)
  })

  useEffect(() => {
    if (chain?.id && !chain?.unsupported) {
      setTargetChain(chain?.id)
    }
  }, [chain?.id, chain?.unsupported])

  useEffect(() => {
    if (!TICKERS_FILTER_BY_CHAIN[targetChain].find((option) => option.name === ticker)) {
      setTicker(TICKERS_FILTER_BY_CHAIN[targetChain][0].name)
    }
  }, [setTicker, targetChain, ticker])

  return (
    <LeftWrapper>
      <Header />
      <Halo />
      <Stack spacing={32} sx={{ width: '100%', alignItems: 'center', mt: 32 }}>
        <NetworkSelect
          tag="From"
          value={targetChain}
          onChange={(event) => {
            if (typeof event.target.value === 'number') {
              setTargetChain(event.target.value)
            }
          }}
        />
        <GoToWithdraw />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={20} width="100%">
          <BtcSelect tag="To" />
          <AssetSelect mode="evm" />
        </Stack>
      </Stack>

      <MinimumAmountWrapper>
        <Text3 sx={{ opacity: 0.5 }}>Available</Text3>
        <Text3>
          {brc20Balance && chain?.id && !isBrc20BalanceLoading
            ? commify(Number(formatUnits(brc20Balance, getTokenDecimals(ticker, chain?.id))).toFixed(2))
            : '-'}{' '}
          {TICKERS_MAP_BY_NAME?.[ticker].erc20Tokens?.[chain?.id]?.symbol}
        </Text3>
      </MinimumAmountWrapper>

      <NumberInput
        fullWidth
        sx={{ mt: 32 }}
        startAdornment={<Tag sx={{ mr: 10 }}>Amount</Tag>}
        endAdornment={
          <MaxButton
            onClick={() => {
              if (brc20Balance && chain?.id) {
                setAmountInput(formatUnits(brc20Balance, getTokenDecimals(ticker, chain?.id)))
              }
            }}
          />
        }
        placeholder="0"
        value={amountInput}
        onUserInput={setAmountInput}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        marginTop={10}
        flexWrap="wrap"
        columnGap={14}
        rowGap={4}
      >
        <Stack direction="row" alignItems="center" spacing={4}>
          <ElectricalServicesIcon sx={{ color: '#BFFF0B' }} />
          <Text3>
            Service fees: {fee ? formatEther(fee?.toString()) : '-'} {CHAIN_INFO?.[targetChain].nativeCurrency.symbol}
          </Text3>
          <Tooltip
            placement="top"
            title="We will deduct the corresponding handling fee as the gas fee for your transfer"
          >
            <ErrorOutlineIcon sx={{ width: 16, height: 16, opacity: 0.5 }} />
          </Tooltip>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={4}>
          <Text3 sx={{ opacity: 0.5 }}>You will receive</Text3>
          <Text3>
            {commify(amountInput) || 0} {TICKERS_MAP_BY_NAME?.[ticker].name}
          </Text3>
          <Brc20Tag>
            <Text3 sx={{ color: 'rgba(191, 255, 11, 0.5)' }}>BRC 20</Text3>
          </Brc20Tag>
        </Stack>
      </Stack>
      <OutlinedInput
        fullWidth
        sx={{ mt: 20 }}
        placeholder="Enter BRC-20 Recipient Address"
        value={addressInput}
        onChange={(event) => {
          setAddressInput(event.target.value)
        }}
      />
      <CheckRowWrapper sx={{ mt: 20 }}>
        <Checkbox
          sx={{
            '&:hover': { bgcolor: 'transparent' },
            p: 0,
          }}
          disableRipple
          checked={isChecked}
          onChange={toggleChecked}
          checkedIcon={<BpCheckedIcon />}
          icon={<BpIcon />}
        />
        <Text2 color="#FA516F">I have learned that service is only available to non-U.S. persons and entities.</Text2>
      </CheckRowWrapper>

      <ShowConfirmDialogButton
        withdrawAmount={amountInput}
        receiverAddress={addressInput}
        isChecked={isChecked}
        // assetToken={TICKER_ERC_NAME[ticker]}
        tickerErcName={TICKERS_MAP_BY_NAME[ticker].erc20Tokens[chain?.id]?.symbol}
        tokenName={ticker}
        brc20Balance={brc20Balance}
        targetChain={targetChain}
      />

      <PendingHistory />
    </LeftWrapper>
  )
}
